import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  ArrowUturnLeftIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import {
  setCommand,
  setMailMessageItemsFilterLoad,
  setMailMessageItemsLoad,
} from "../../redux/mailSlice";
import { getUserdetails } from "../../Localstorage/localstorage";
import ListChats from "../../Componenten/ListChats";
import { wa } from "./mailWaTheme";
import { avatarInitial, contactAvatarUrl } from "../../utils/mailContactAvatar";
import { AXIOS_GET, AXIOS_POST } from "../../server/api/crud";
import { storage } from "../../firebase/firebase";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MailList = ({ setOpen, mobileChatOpen, onExitMobileChat }) => {
  const dispatch = useDispatch();
  const userdetails = getUserdetails();
  const fileInputRef = useRef(null);
  const chatScrollRef = useRef(null);
  const forceScrollAfterSendRef = useRef(false);
  const [composerText, setComposerText] = useState("");
  const [composerImageData, setComposerImageData] = useState("");
  const [composerImageName, setComposerImageName] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const isIPadDevice = () => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    return /iPad/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  };
  const isTouchTabletViewport = () => {
    if (typeof window === "undefined") return false;
    const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
    return Boolean(coarsePointer && window.innerWidth <= 1366);
  };
  const shouldUseBottomComposer = () => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 768 || isIPadDevice() || isTouchTabletViewport();
  };
  const [isCompactComposerMode, setIsCompactComposerMode] = useState(() =>
    typeof window !== "undefined" ? shouldUseBottomComposer() : false
  );

  const { mailMessage, mailMessageItemsFilter, mailMessageItems } = useSelector(
    (state) => state.mail
  );

  useEffect(() => {
    const filterMessageItems = mailMessageItemsFilter.filter(
      (mail) => mail?.mailmessage_id === mailMessage?.id
    );
    dispatch(setMailMessageItemsLoad(filterMessageItems));
  }, [mailMessage, mailMessageItemsFilter, dispatch]);

  useEffect(() => {
    const onResize = () => setIsCompactComposerMode(shouldUseBottomComposer());
    window.addEventListener("resize", onResize);
    // re-evaluate once after mount (some iPads report values after first paint)
    const t = window.setTimeout(() => onResize(), 60);
    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    if (!mailMessage?.id) {
      setComposerText("");
      setComposerImageData("");
      setComposerImageName("");
    }
  }, [mailMessage?.id]);

  const scrollToTop = useCallback((behavior = "auto") => {
    const container = chatScrollRef.current;
    if (!container) return;
    container.scrollTo({ top: 0, behavior });
  }, []);

  useEffect(() => {
    scrollToTop("auto");
    const raf = window.requestAnimationFrame(() => scrollToTop("auto"));
    const t = window.setTimeout(() => scrollToTop("auto"), 140);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [mailMessage?.id, mailMessageItems?.length, scrollToTop]);

  const openModal = (p_type = "insert") => {
    dispatch(setCommand(p_type));
    setOpen(true);
  };

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const canSend = useMemo(
    () => Boolean(mailMessage?.id && (composerText.trim() || composerImageData) && !isSending && !isUploadingImage),
    [mailMessage?.id, composerText, composerImageData, isSending, isUploadingImage]
  );

  const handleSelectImage = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploadingImage(true);
    setComposerImageName(file.name || "afbeelding");
    const timestamp = Date.now();
    const safeFileName = String(file.name || "image").replace(/\s+/g, "_");
    const folder = `${userdetails?.full_name || "chat"}/${userdetails?.user_id || "unknown"}/mailbox/${mailMessage?.id || "thread"}`;
    const fileRef = ref(storage, `${folder}/${timestamp}-${safeFileName}`);
    const uploadTask = uploadBytesResumable(fileRef, file);
    uploadTask.on(
      "state_changed",
      () => {},
      (error) => {
        console.error("Firebase image upload failed", error);
        setComposerImageData("");
        setComposerImageName("");
        setIsUploadingImage(false);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setComposerImageData(url);
        setIsUploadingImage(false);
      }
    );
    event.target.value = "";
  }, [mailMessage?.id, userdetails?.full_name, userdetails?.user_id]);

  const sendMobileMessage = useCallback(async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      const textHtml = escapeHtml(composerText).replace(/\n/g, "<br/>");
      const imageHtml = composerImageData
        ? `<div style="margin-top:8px"><img src="${composerImageData}" alt="${escapeHtml(
            composerImageName || "afbeelding"
          )}" style="max-width:220px;border-radius:10px" /></div>`
        : "";

      const newItemMessage = {
        mailmessage_id: mailMessage?.id,
        author_id: userdetails?.user_id,
        author: userdetails?.full_name,
        avatar: userdetails?.avatar,
        body: `${textHtml}${imageHtml}`.trim(),
      };
      const optimisticItem = {
        ...newItemMessage,
        id: `local-${Date.now()}`,
        datetime: new Date().toISOString(),
        status: "send",
      };

      const response = await AXIOS_POST("mailbox/message", newItemMessage);
      if (response?.status === 200 || response?.status === 201) {
        const itemsFromApi = response?.data?.data?.thread_items;
        let items = Array.isArray(itemsFromApi) && itemsFromApi.length > 0 ? itemsFromApi : null;

        // Backward compatibility for older backend response shape.
        if (!items) {
          try {
            const refreshed = await AXIOS_GET(`mailbox/mailmessage/${mailMessage?.id}/items`);
            const refreshedItems = refreshed?.data?.data;
            if (Array.isArray(refreshedItems) && refreshedItems.length > 0) {
              items = refreshedItems;
            }
          } catch (error) {
            // Ignore and fallback to local optimistic item below
          }
        }

        if (!items) {
          items = [optimisticItem, ...mailMessageItems];
        }
        dispatch(setMailMessageItemsFilterLoad(items));
        dispatch(setMailMessageItemsLoad(items));
        forceScrollAfterSendRef.current = true;
        scrollToTop("smooth");
        window.requestAnimationFrame(() => scrollToTop("smooth"));
        window.setTimeout(() => {
          scrollToTop("auto");
          forceScrollAfterSendRef.current = false;
        }, 300);
        setComposerText("");
        setComposerImageData("");
        setComposerImageName("");
      }
    } finally {
      setIsSending(false);
    }
  }, [
    canSend,
    composerImageData,
    composerImageName,
    composerText,
    dispatch,
    mailMessage?.id,
    mailMessageItems,
    scrollToTop,
    userdetails?.avatar,
    userdetails?.full_name,
    userdetails?.user_id,
  ]);

  const subtitle =
    userdetails.role_id === 1
      ? mailMessage?.sender || "Geen patient"
      : mailMessage?.receiver || "Geen specialist";

  const title = mailMessage?.subject || "Berichten";

  const headerAvatarUrl = useMemo(
    () => contactAvatarUrl(mailMessage, userdetails?.user_id),
    [mailMessage, userdetails?.user_id]
  );

  return (
    <section
      aria-labelledby="message-heading"
      className={classNames(
        "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-l",
        wa.border,
        wa.app,
        mobileChatOpen ? "flex" : "hidden md:flex"
      )}
    >
      <header
        className={classNames(
          "flex flex-shrink-0 items-center gap-2 border-b px-2 py-2 sm:px-3",
          wa.border,
          wa.panel2
        )}
      >
        <button
          type="button"
          onClick={onExitMobileChat}
          className={classNames(
            "rounded-full p-2 md:hidden",
            wa.muted,
            wa.hover,
            "hover:text-wa-text"
          )}
          aria-label="Terug naar gesprekken"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-full border border-wa-border bg-gradient-to-br from-wa-panel2 to-wa-panel">
            {headerAvatarUrl ? (
              <img
                src={headerAvatarUrl}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div
                className={classNames(
                  "flex h-full w-full items-center justify-center text-sm font-semibold",
                  wa.text
                )}
              >
                {avatarInitial(subtitle)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h1 id="message-heading" className={classNames("truncate text-[15px] font-semibold sm:text-base", wa.text)}>
              {title}
            </h1>
            <p className={classNames("truncate text-xs", wa.muted)}>Contact: {subtitle}</p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          {mailMessage?.category ? (
            <span
              className={classNames(
                "mr-1 hidden max-w-[8rem] truncate rounded-full px-2 py-0.5 text-xs font-medium sm:inline",
                wa.panel,
                wa.muted
              )}
            >
              {mailMessage.category}
            </span>
          ) : null}
          <button
            type="button"
            className={classNames(
              "hidden rounded-full p-2 sm:inline-flex",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
            aria-label="Zoeken in gesprek"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>
          {mailMessage && !isCompactComposerMode ? (
            <button
              type="button"
              onClick={() => openModal("update")}
              title="Beantwoorden"
              className={classNames(
                "rounded-full p-2 transition-colors",
                wa.muted,
                wa.hover,
                "hover:text-wa-text"
              )}
            >
              <ArrowUturnLeftIcon className="h-5 w-5" />
            </button>
          ) : null}
          <button
            type="button"
            className={classNames(
              "rounded-full p-2",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
            aria-label="Meer"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>
      </header>

      {!mailMessage ? (
        <div
          className={classNames(
            "flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center",
            wa.muted
          )}
        >
          <div className={classNames("rounded-full p-6", wa.panel)}>
            <ArrowUturnLeftIcon className="h-10 w-10 opacity-40" aria-hidden />
          </div>
          <p className="max-w-sm text-sm">
            Selecteer een gesprek in de lijst om berichten te bekijken, of start een nieuw gesprek.
          </p>
        </div>
      ) : (
        <div
          className="relative min-h-0 flex-1 overflow-hidden bg-wa-app"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgb(var(--wa-border) / 0.35) 1.05px, transparent 1.05px)",
            backgroundSize: "44px 44px",
          }}
        >
          <div
            ref={chatScrollRef}
            className={classNames("h-full min-h-0 overflow-y-auto overscroll-contain", isCompactComposerMode ? "pb-28" : "")}
            onLoadCapture={(event) => {
              if (!forceScrollAfterSendRef.current) return;
              if (event?.target?.tagName === "IMG") {
                scrollToTop("auto");
              }
            }}
          >
            <ListChats chats={mailMessageItems} user={userdetails} />
          </div>
        </div>
      )}
      {isCompactComposerMode && mailMessage ? (
        <footer className={classNames("border-t px-2 py-2 shadow-[0_-8px_20px_rgba(0,0,0,0.2)]", wa.border, wa.panel2)}>
          {composerImageData ? (
            <div className="mb-2 flex items-center gap-2 rounded-md border border-wa-border bg-wa-panel p-2">
              <img src={composerImageData} alt="preview" className="h-12 w-12 rounded object-cover" />
              <span className={classNames("min-w-0 flex-1 truncate text-xs", wa.muted)}>
                {composerImageName || "Afbeelding"}
              </span>
              <button
                type="button"
                onClick={() => {
                  setComposerImageData("");
                  setComposerImageName("");
                }}
                className={classNames("text-xs font-medium", wa.accent)}
              >
                Verwijder
              </button>
            </div>
          ) : null}
          {isUploadingImage ? (
            <div className={classNames("mb-2 rounded-md border border-wa-border bg-wa-panel px-3 py-2 text-xs", wa.muted)}>
              Afbeelding uploaden naar Firebase...
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={classNames(
                "inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
                wa.muted,
                wa.hover,
                "hover:text-wa-text"
              )}
              aria-label="Afbeelding toevoegen"
            >
              <PhotoIcon className="h-5 w-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleSelectImage}
            />
            <textarea
              rows={1}
              value={composerText}
              onChange={(e) => setComposerText(e.target.value)}
              placeholder="Typ een bericht..."
              className="max-h-28 min-h-[40px] flex-1 resize-none rounded-2xl border border-wa-border bg-wa-panel px-3 py-2 text-sm text-wa-text outline-none placeholder:text-wa-muted focus:border-wa-accent"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMobileMessage();
                }
              }}
            />
            <button
              type="button"
              onClick={sendMobileMessage}
              disabled={!canSend}
              className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-wa-accent text-wa-onaccent transition disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Versturen"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </button>
          </div>
        </footer>
      ) : null}
    </section>
  );
};

export default MailList;
